import { computeMetrics, computeStewardshipScore } from "@/lib/finance";
import { computeLifeMetrics, periodKey } from "@/lib/life";
import { computeWellnessScore } from "@/lib/wellness";
import { iso } from "@/lib/wellness";

// Build a combined snapshot across all three command centers for a given date.
export function computeCentralSnapshot(fin, life, wellness, date = iso()) {
  const steward = computeStewardshipScore(fin || {});
  const well = computeWellnessScore(wellness || {});
  const overrides = {
    "Wealth & Stewardship": steward.score,
    "Health & Vitality": well.score,
  };
  const lifeM = computeLifeMetrics(life || {}, date, overrides);
  const overall = Math.round((steward.score + lifeM.lifeScore + well.score) / 3);

  return {
    date,
    overall,
    wealth: {
      score: steward.score,
      netWorth: steward.netWorth,
      months: steward.months,
      util: steward.util,
      savingsRate: steward.savingsRate,
      breakdown: steward.breakdown,
    },
    life: {
      score: lifeM.lifeScore,
      energy: Number(life?.energy) || 0,
      alignment: Number(life?.alignment) || 0,
      earned: lifeM.earned,
      planned: lifeM.planned,
      lowest: lifeM.lowest?.area?.name || null,
      areaScores: (lifeM.areaScores || []).map((a) => ({ name: a.area.name, short: a.area.short, score: a.score })),
    },
    wellness: {
      score: well.score,
      readiness: well.readiness,
      goalAvg: well.goalAvg,
      glowPct: well.glowPct,
      breakdown: well.breakdown,
    },
  };
}

export const centerMeta = {
  Wealth: { color: "#1f7355", route: "/wealth" },
  Life: { color: "#c68b3c", route: "/life" },
  Wellness: { color: "#34705f", route: "/wellness" },
};

export const statusStyles = {
  "on-track": { label: "On track", color: "var(--green)", bg: "rgba(31,115,85,0.12)" },
  watch: { label: "Watch", color: "var(--gold)", bg: "rgba(198,139,60,0.14)" },
  "needs-focus": { label: "Needs focus", color: "var(--danger)", bg: "rgba(176,58,46,0.12)" },
};

export function scoreTone(score) {
  return score >= 70 ? "on-track" : score >= 45 ? "watch" : "needs-focus";
}

// Monday-anchored week key (matches life.periodKey semantics).
export function weekKeyOf(dateStr = iso()) {
  return periodKey("Week", dateStr);
}

export function weekRangeLabel(weekKey) {
  const s = new Date(weekKey + "T12:00:00");
  const e = new Date(s); e.setDate(s.getDate() + 6);
  const f = (x) => x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${f(s)} \u2013 ${f(e)}`;
}

// Compare two saved snapshots and return per-metric deltas.
export function diffSnapshots(cur, prev) {
  const pick = (s) => ({
    Overall: s?.overall ?? 0,
    Wealth: s?.wealth?.score ?? 0,
    Life: s?.life?.score ?? 0,
    Wellness: s?.wellness?.score ?? 0,
    "Net worth": s?.wealth?.netWorth ?? 0,
    Readiness: s?.wellness?.readiness ?? 0,
  });
  const c = pick(cur), p = pick(prev || {});
  const money = new Set(["Net worth"]);
  return Object.keys(c).map((k) => ({
    key: k,
    cur: c[k],
    prev: prev ? p[k] : null,
    delta: prev ? c[k] - p[k] : null,
    isMoney: money.has(k),
  }));
}
